import { OfferingCategory } from "@albertine/types";
import type {
    ListRequestsItem,
    Request as RequestDto,
} from "@albertine/types/openapi/lmt";
import type { Request } from "../common/types/Request";
import { unassignedAgent } from "../common/utils/agents.util";
import { isoToFirebaseTimestamp } from "./timestamp.util";

function toOfferingCategory(
    value: string | null | undefined,
): OfferingCategory | null | undefined {
    if (value == null) {
        return value;
    }
    const parsed = OfferingCategory.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}

function toProposal(proposal: RequestDto["proposal"]): Request["proposal"] {
    if (!proposal) {
        return proposal;
    }
    return {
        messages: proposal.messages.map((message) => ({
            message: message.message,
            bookings: message.bookings,
            sentAt: isoToFirebaseTimestamp(message.sentAt),
        })),
    };
}

export function mapApiRequestToRequest(r: RequestDto): Request {
    return {
        id: r.id,
        member: r.memberId,
        conversation: r.conversationId,
        status: r.status,
        ticketNumber: r.ticketNumber,
        archived: r.archived,
        title: r.title,
        startDate: r.startDate,
        startTime: r.startTime,
        endDate: r.endDate,
        endTime: r.endTime,
        offeringCategory: toOfferingCategory(r.offeringCategory),
        numberOfGuests: r.numberOfGuests,
        createdAt: isoToFirebaseTimestamp(r.createdAt),
        assignedTo: r.assignedToAgentId,
        createdFromMessage: r.createdFromMessageId,
        firstChoice: r.firstChoice,
        notes: r.notes,
        budget: r.budget,
        outcome: r.outcome,
        bookingOutcome: r.bookingOutcome,
        source: r.source,
        sourceChannel: r.sourceChannel,
        nextDeadlineStep: r.nextDeadlineStep,
        nextDeadlineAt: isoToFirebaseTimestamp(r.nextDeadlineAt),
        deadlineCreatedAt: isoToFirebaseTimestamp(r.deadlineCreatedAt),
        locationGooglePlaceId: r.locationGooglePlaceId,
        locationGoogleName: r.locationGoogleName,
        locationGoogleFormattedAddress: r.locationGoogleFormattedAddress,
        locationGoogleAddressComponents: r.locationGoogleAddressComponents,
        locationGoogleGeometryLat: r.locationGoogleGeometryLat,
        locationGoogleGeometryLng: r.locationGoogleGeometryLng,
        highlightedFields: r.highlightedFields,
        aiCaptureStatus: r.aiCaptureStatus,
        memberRating: r.memberRating,
        memberFeedback: r.memberFeedback,
        memberFeedbackTimestamp: isoToFirebaseTimestamp(r.memberFeedbackTimestamp),
        proposalBookings: r.proposalBookings,
        proposal: toProposal(r.proposal),
    };
}

export function mapListRequestsItemToRequest(item: ListRequestsItem): Request {
    return {
        id: item.id,
        member: item.member,
        conversation: item.conversation,
        status: item.status,
        ticketNumber: item.ticketNumber,
        archived: item.archived ?? false,
        title: item.title ?? null,
        startDate: item.startDate ?? null,
        startTime: item.startTime ?? null,
        endDate: item.endDate ?? null,
        endTime: item.endTime ?? null,
        offeringCategory: toOfferingCategory(item.offeringCategory),
        numberOfGuests: item.numberOfGuests ?? null,
        // The list endpoint sends a sentinel where Firestore stored no assignee.
        assignedTo:
            !item.assignedTo || item.assignedTo === unassignedAgent.id
                ? null
                : item.assignedTo,
        assignedToName: item.assignedToName ?? null,
        createdAt: isoToFirebaseTimestamp(item.createdAt),
        outcome: item.outcome,
        bookingOutcome: item.bookingOutcome,
        nextDeadlineStep: item.nextDeadlineStep,
        nextDeadlineAt: isoToFirebaseTimestamp(item.nextDeadlineAt),
        memberRating: item.memberRating,
        memberFeedbackTimestamp: isoToFirebaseTimestamp(item.memberFeedbackTimestamp),
        locationGoogleFormattedAddress: item.locationGoogleFormattedAddress,
    };
}
